/**
 * @fileoverview Modal ENXUTO de upgrade de assentos (E3b). Diferente do
 * PlanCheckoutModal, NÃO coleta cartão nem endereço — a assinatura já existe e o
 * cartão salvo é reutilizado. Só ajusta a quantidade de usuários contratados:
 * stepper + preço ao vivo (/account/quote) → POST /account/upgrade. O novo valor
 * entra no próximo ciclo (sem cobrança imediata). Reusável por:
 *  - UsersManager (reativo: abre ao bater no teto; onUpgraded re-tenta criar);
 *  - aba Faturamento (proativo: botão "Gerenciar assentos").
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, makeStyles } from "@material-ui/core";
import { X, Minus, Plus, Sparkles, Loader, CheckCircle2, ArrowUpCircle } from "lucide-react";

import api from "../../../services/api";
import { i18n } from "../../../translate/i18n";
import { createModalDesign } from "../../../styles/modalDesign";

const fmtBRL = cents =>
  cents == null ? "—" : (cents / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const useStyles = makeStyles(theme => {
  const md = createModalDesign(theme);
  const dark = theme.palette.type === "dark";
  const border = dark ? "rgba(255,255,255,0.1)" : "#e5e7eb";
  return {
    dialog: { ...md.mdDialog, "& .MuiDialog-paper": { ...md.mdDialog["& .MuiDialog-paper"], width: "min(460px, calc(100vw - 32px))" } },
    head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: theme.spacing(3.5, 3.5, 0) },
    title: { fontSize: "1.25rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827", display: "flex", alignItems: "center", gap: 8 },
    desc: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.65)" : "#6b7280", marginTop: 6, lineHeight: 1.5 },
    close: { cursor: "pointer", color: dark ? "rgba(248,250,252,0.6)" : "#9ca3af", display: "flex" },
    body: { padding: theme.spacing(2.5, 3.5, 3.5), display: "flex", flexDirection: "column", gap: theme.spacing(2.25) },
    planRow: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.75)" : "#374151", background: dark ? "rgba(255,255,255,0.04)" : "#f9fafb", borderRadius: 10, padding: theme.spacing(1.25, 1.5), display: "flex", alignItems: "center", gap: 8 },
    seatRow: { display: "flex", alignItems: "center", justifyContent: "space-between" },
    seatLabel: { fontSize: "0.9rem", fontWeight: 600, color: dark ? "#f8fafc" : "#111827" },
    seatHint: { fontSize: "0.74rem", color: dark ? "rgba(248,250,252,0.5)" : "#9ca3af", marginTop: 2 },
    stepper: { display: "flex", alignItems: "center", gap: theme.spacing(1.5) },
    stepBtn: { width: 34, height: 34, borderRadius: 8, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", background: dark ? "rgba(255,255,255,0.08)" : "#f3f4f6", color: dark ? "#f8fafc" : "#111827", "&:disabled": { opacity: 0.4, cursor: "not-allowed" } },
    stepVal: { minWidth: 36, textAlign: "center", fontSize: "1.2rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827" },
    priceBox: { borderTop: `1px solid ${border}`, paddingTop: theme.spacing(2), display: "flex", alignItems: "flex-end", justifyContent: "space-between" },
    priceTotal: { fontSize: "1.7rem", fontWeight: 800, color: dark ? "#f8fafc" : "#111827", lineHeight: 1 },
    pricePer: { fontSize: "0.85rem", color: dark ? "rgba(248,250,252,0.6)" : "#6b7280", fontWeight: 500 },
    priceBreak: { fontSize: "0.76rem", color: dark ? "rgba(248,250,252,0.55)" : "#9ca3af", textAlign: "right" },
    cta: { ...md.mdBtnPrimary, height: 46, width: "100%", fontSize: "0.98rem", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 },
    error: { fontSize: "0.82rem", color: "#ef4444" },
    note: { fontSize: "0.76rem", color: dark ? "rgba(248,250,252,0.5)" : "#9ca3af" },
    center: { textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: theme.spacing(1.5), padding: theme.spacing(2, 0) },
    successIcon: { color: "#22c55e" }
  };
});

const UpgradeModal = ({ open, onClose, suggestedTotal, onUpgraded, mode = "attendance" }) => {
  const classes = useStyles();
  const isInternal = mode === "internal";

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null); // { tier, ciclo, currentSeats, internos, addon }
  const [target, setTarget] = useState(0);
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const debounceRef = useRef();

  // Carrega o contexto do plano ao abrir.
  useEffect(() => {
    if (!open) return;
    setLoading(true); setError(""); setDone(false); setQuote(null);
    api.get("/account/billing")
      .then(({ data }) => {
        const p = data?.plan || {};
        const current = Number(p.seats) || 0;
        const ctx = {
          tier: p.tier === "pro" ? "pro" : "basic",
          ciclo: p.ciclo === "anual" ? "anual" : "mensal",
          currentSeats: current,
          seatsContracted: Number(p.seats_contracted) || current,
          internos: Number(p.qtd_internos) || 0,
          internosContracted: Number(p.internos_contracted) || 0,
          addon: !!p.addon_chat_interno
        };
        setPlan(ctx);
        const base = isInternal ? ctx.internosContracted : current;
        setTarget(Math.max(base + 1, Number(suggestedTotal) || 0));
      })
      .catch(() => setError(i18n.t("onboarding.upgrade.loadError")))
      .finally(() => setLoading(false));
  }, [open, suggestedTotal, isInternal]);

  // Preço ao vivo do novo total.
  const loadQuote = useCallback(() => {
    if (!plan || !target) return;
    // Atendimento: varia os humanos; Interno: humanos fixos (contratado), varia os internos.
    const params = isInternal
      ? { tier: plan.tier, ciclo: plan.ciclo, qtd_usuarios: plan.seatsContracted, qtd_internos: target, addon: true }
      : { tier: plan.tier, ciclo: plan.ciclo, qtd_usuarios: target, qtd_internos: plan.internos, addon: plan.addon };
    api.post("/account/quote", params).then(({ data }) => setQuote(data)).catch(() => setQuote(null));
  }, [plan, target, isInternal]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadQuote, 200);
    return () => clearTimeout(debounceRef.current);
  }, [loadQuote]);

  if (!open) return null;

  const min = plan ? (isInternal ? plan.internosContracted + 1 : plan.currentSeats + 1) : 1;
  const annual = plan?.ciclo === "anual";
  const monthly = quote?.valor_cents;

  const handleConfirm = async () => {
    setError(""); setSubmitting(true);
    try {
      if (isInternal) await api.post("/account/internos-upgrade", { qtd_internos: target });
      else await api.post("/account/upgrade", { qtd_usuarios: target });
      setDone(true);
      if (onUpgraded) { try { await onUpgraded(); } catch (_) { /* re-tentativa é best-effort */ } }
      setTimeout(() => { onClose && onClose(); }, 1400);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && (detail.message || (typeof detail === "string" ? detail : JSON.stringify(detail)))) || e?.response?.data?.error || e.message;
      setError(i18n.t("onboarding.upgrade.confirmError") + (msg ? `: ${msg}` : "."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open disableEscapeKeyDown className={classes.dialog} maxWidth={false} onClose={() => !submitting && onClose && onClose()}>
      {!done && (
        <div className={classes.head}>
          <div>
            <div className={classes.title}><ArrowUpCircle size={20} /> {isInternal ? i18n.t("onboarding.upgrade.titleInternal") : i18n.t("onboarding.upgrade.titleAttendance")}</div>
            <div className={classes.desc}>{isInternal ? i18n.t("onboarding.upgrade.descInternal") : i18n.t("onboarding.upgrade.descAttendance")}{i18n.t("onboarding.upgrade.descNextCharge")}</div>
          </div>
          <span className={classes.close} onClick={() => !submitting && onClose && onClose()} title={i18n.t("onboarding.upgrade.close")}><X size={20} /></span>
        </div>
      )}

      <div className={classes.body}>
        {loading ? (
          <div className={classes.center}><Loader size={26} /></div>
        ) : done ? (
          <div className={classes.center}>
            <CheckCircle2 size={52} className={classes.successIcon} />
            <div className={classes.seatLabel} style={{ fontSize: "1.05rem" }}>{i18n.t("onboarding.upgrade.successTitle")}</div>
            <div className={classes.desc} style={{ textAlign: "center" }}>{isInternal ? i18n.t("onboarding.upgrade.successInternal", { count: target }) : i18n.t("onboarding.upgrade.successAttendance", { count: target })}</div>
          </div>
        ) : !plan ? (
          <div className={classes.error}>{error || i18n.t("onboarding.upgrade.planUnavailable")}</div>
        ) : (
          <>
            <div className={classes.planRow}>
              <Sparkles size={16} style={{ color: "#7b2dff" }} />
              {isInternal
                ? i18n.t("onboarding.upgrade.planRowInternal", {
                    tier: plan.tier === "pro" ? "Pro" : "Basic",
                    ciclo: plan.ciclo === "anual" ? i18n.t("onboarding.upgrade.cycleAnnual") : i18n.t("onboarding.upgrade.cycleMonthly"),
                    count: plan.internosContracted
                  })
                : i18n.t("onboarding.upgrade.planRowAttendance", {
                    tier: plan.tier === "pro" ? "Pro" : "Basic",
                    ciclo: plan.ciclo === "anual" ? i18n.t("onboarding.upgrade.cycleAnnual") : i18n.t("onboarding.upgrade.cycleMonthly"),
                    count: plan.currentSeats,
                    usersWord: plan.currentSeats === 1 ? i18n.t("onboarding.upgrade.userSingular") : i18n.t("onboarding.upgrade.userPlural")
                  })}
            </div>

            <div className={classes.seatRow}>
              <div>
                <div className={classes.seatLabel}>{isInternal ? i18n.t("onboarding.upgrade.newTotalInternal") : i18n.t("onboarding.upgrade.newTotalAttendance")}</div>
                <div className={classes.seatHint}>{i18n.t("onboarding.upgrade.minHint", { min })}</div>
              </div>
              <div className={classes.stepper}>
                <button className={classes.stepBtn} onClick={() => setTarget(t => Math.max(min, t - 1))} disabled={target <= min}><Minus size={16} /></button>
                <span className={classes.stepVal}>{target}</span>
                <button className={classes.stepBtn} onClick={() => setTarget(t => t + 1)}><Plus size={16} /></button>
              </div>
            </div>

            <div className={classes.priceBox}>
              <div>
                <div className={classes.priceTotal}>{fmtBRL(monthly)}</div>
                <div className={classes.pricePer}>{i18n.t("onboarding.upgrade.perMonth")}{annual ? i18n.t("onboarding.upgrade.billedAnnually") : ""}</div>
              </div>
              {quote && (
                <div className={classes.priceBreak}>
                  {isInternal
                    ? i18n.t("onboarding.upgrade.breakInternal", {
                        addon: quote.addon_qtd,
                        seats: quote.billable_seats,
                        ai: plan.tier === "pro" ? i18n.t("onboarding.upgrade.aiSuffix") : ""
                      })
                    : i18n.t("onboarding.upgrade.breakAttendance", {
                        seats: quote.billable_seats,
                        ai: plan.tier === "pro" ? i18n.t("onboarding.upgrade.aiSuffix") : ""
                      })}
                </div>
              )}
            </div>

            {error && <div className={classes.error}>{error}</div>}

            <button className={classes.cta} onClick={handleConfirm} disabled={submitting || !quote}>
              {submitting ? <Loader size={18} /> : <ArrowUpCircle size={18} />}
              {submitting ? i18n.t("onboarding.upgrade.processing") : i18n.t("onboarding.upgrade.confirm")}
            </button>
            <div className={classes.note}>{i18n.t("onboarding.upgrade.footnote")}</div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default UpgradeModal;
